import { useTranslation } from 'react-i18next';
import { useGetInventoryAirline } from '@/api/generated/inventory-airlines/inventory-airlines';
import { Alert } from '@/shared/ui';
import { DETAIL_QUERY_STALE_TIME_MS } from '@/shared/lib/queryClient';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { useWindowTitle } from '@/shared/windowing';
import { airlineDetailAgenda } from './airlineDetailAgenda';
import { AirlineDetailView } from './AirlineDetailView';
import { emptyAirlineFormValues, fromGetAirlineResponse } from './airlineFormSchema';

interface AirlineDetailPageProps {
  layerId: string;
}

/** Thin loader: vyřeší parametr agendy a (v editaci) načtení detailu letecké společnosti. */
export function AirlineDetailPage({ layerId }: AirlineDetailPageProps) {
  const { t } = useTranslation('inventory');
  const { open } = useAgendaParams(airlineDetailAgenda, layerId);
  const airlineId = open.airlineId;
  const isEdit = airlineId != null;

  // Krátký staleTime (jednotky sekund) — detail se při každém otevření načte znovu, cache jen pro
  // rychlé překlikávání. Po uložení navíc detail invalidujeme (viz AirlineDetailView.onSaved).
  const airlineQuery = useGetInventoryAirline(airlineId ?? 0, {
    query: { enabled: isEdit, staleTime: DETAIL_QUERY_STALE_TIME_MS },
  });

  useWindowTitle(airlineQuery.data?.tradeName);

  if (isEdit && airlineQuery.isLoading) {
    return <p className="text-phx-text-muted text-phx-sm p-phx-5">{t('airline.loading')}</p>;
  }
  if (isEdit && airlineQuery.isError) {
    return (
      <div className="p-phx-5">
        <Alert severity="error" title={t('airline.loadErrorTitle')}>
          {t('airline.loadErrorDescription')}
        </Alert>
      </div>
    );
  }

  return (
    <AirlineDetailView
      layerId={layerId}
      airlineId={airlineId}
      initialValues={
        airlineQuery.data ? fromGetAirlineResponse(airlineQuery.data) : emptyAirlineFormValues()
      }
      airline={airlineQuery.data}
    />
  );
}
